import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

export interface IModalAction {
  label: string;
  /** Ruta a la que navegar después de cerrar. Sin ruta, solo cierra el modal. */
  route?: string;
  /** Valor que recibe el afterClosed() del caller. */
  result?: unknown;
}

export interface IModalConfig {
  /** Solo define el ícono del modal. */
  type: ModalType;
  title?: string;
  subtitle?: string;
  /**
   * N° de expediente. El modal lo rotula y lo separa del cuerpo.
   *
   * DEUDA TÉCNICA: hoy lo usa solo cambio de plan. Celiaquía, unificación, desunificación,
   * cambio de forma de pago y la tabla de autorizaciones siguen armando ese mismo texto a mano
   * dentro de `text`, cada uno con su propio `<br /> <br />` y uno de ellos con `Nº` en lugar
   * de `N°`. Migrarlos a este campo para que se vea igual en todos los trámites.
   */
  expNro?: number | string;
  text?: string;
  /** Muestra el texto en un tamaño mayor, para mensajes que el usuario no debe pasar por alto. */
  highlightText?: boolean;
  /**
   * Botones del modal. Un array vacío no renderiza ninguno, para los casos en los que la única
   * salida es un link dentro del texto. Sin definir, se usan los botones por defecto del type.
   */
  actions?: IModalAction[];

  /** @deprecated El texto está hardcodeado en el modal. Pasarlo por `text`. */
  showGenericMessage?: boolean;
  /** @deprecated Alcanza con no mandar `title`. */
  hideTitle?: boolean;
  /** @deprecated Reemplazado por la `route` de cada action. */
  stopRedirect?: boolean;
}

export type ModalType = 'success' | 'error' | 'more-info';

interface IModalIcon {
  src: string;
  width: number;
  height?: number;
}

const HOME_ROUTE = '/dashboard/mi-panel';

/**
 * Mensaje para los errores que llegan sin título ni texto, que si no muestran un modal con
 * el ícono y el botón solos. Son los textos que tenía el template genérico del modal, que
 * quedó huérfano al agregarse el caso de título más texto.
 *
 * DEUDA TÉCNICA: celiaquía, unificación de aportes, plan materno y reintegros llaman al modal
 * con `{ type: 'error' }` pelado desde el error de su submit. Deberían mandar su propio
 * mensaje, y ahí este default deja de hacer falta.
 */
const DEFAULT_ERROR = {
  title: 'Hubo un error en tu <strong>solicitud</strong>',
  text: 'Vuelve a intentarlo.'
};

const ICONS: Record<ModalType, IModalIcon> = {
  'success': { src: 'assets/svg/check.svg', width: 58 },
  'error': { src: 'assets/svg/alert.svg', width: 43, height: 56 },
  'more-info': { src: 'assets/svg/check-document.svg', width: 43, height: 56 }
};

@Component({
  selector: 'nobis-alert-modal',
  templateUrl: './alert-modal.component.html',
  styleUrls: ['./alert-modal.component.scss']
})
export class AlertModalComponent {

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IModalConfig,
    private dialogRef: MatDialogRef<AlertModalComponent>,
    private router: Router
  ) {
    // Los modales de éxito no se cierran clickeando afuera ni con Esc, sólo con su botón. Así se
    // evita que el usuario vuelva a la pantalla con el formulario completo y lo reenvíe.
    if (this.data.type === 'success') this.dialogRef.disableClose = true;
  }

  get icon(): IModalIcon {
    return ICONS[this.data.type];
  }

  /**
   * COMPATIBILIDAD: los errores que llegan sin `title` muestran su texto como encabezado,
   * que es como se ven hoy. Cuando esos callers manden título y texto por separado, `heading`
   * pasa a ser `data.title` y `body` a ser `data.text`, y estos dos getters se eliminan.
   */
  get heading(): string | undefined {
    if (this.isEmptyError) return DEFAULT_ERROR.title;
    if (this.data.hideTitle) return undefined;
    return this.data.title || (this.data.type === 'error' ? this.data.text : undefined);
  }

  get body(): string | undefined {
    if (this.isEmptyError) return DEFAULT_ERROR.text;
    const isErrorWithoutTitle = this.data.type === 'error' && !this.data.title;
    return isErrorWithoutTitle ? undefined : this.data.text;
  }

  private get isEmptyError(): boolean {
    return this.data.type === 'error' && !this.data.title && !this.data.text;
  }

  /** Botones por defecto de cada type, para los callers que todavía no definen sus actions. */
  get actions(): IModalAction[] {
    if (!!this.data.actions) return this.data.actions;

    const route = this.data.stopRedirect ? undefined : HOME_ROUTE;

    switch (this.data.type) {
      case 'success':
        return [{ label: 'ACEPTAR', route, result: true }];
      case 'more-info':
        return [{ label: 'CONTINUAR', result: false }];
      default:
        return !!this.data.title || !!this.data.text
          ? [{ label: 'CONTINUAR' }]
          : [{ label: 'CONTINUAR', route, result: false }];
    }
  }

  onAction(action: IModalAction): void {
    this.dialogRef.close(action.result);
    if (!!action.route) this.router.navigate([action.route]);
  }

  /**
   * Los links externos del contenido cierran el modal, ya que el texto se renderiza con
   * innerHTML y no admite un binding propio. Los routerLink internos quedan afuera porque
   * resuelven su propia navegación.
   */
  @HostListener('click', ['$event'])
  onContentClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement)?.closest?.('a[target="_blank"]');
    if (!!anchor) this.onClose(!this.data.stopRedirect);
  }

  onClose(redirect: boolean = false): void {
    this.dialogRef.close(this.data.type === 'success');
    if (redirect) this.router.navigate([HOME_ROUTE]);
  }

}
